import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { app } from '../../config';
import getHeaders from '../../utils/authHeaders';
import { useNavigate } from 'react-router-dom';
import { UpdateRecord } from '../../layouts/UpdateRecord';
import { InputField, SelectField, TextAreaField } from '../../layouts/FormFields';
import { Button, Space, Upload, message, ColorPicker, Select, Switch, Checkbox, Form } from 'antd'; // 4.24.7
import { DeleteFilled, UploadOutlined } from '@ant-design/icons';
import DividerLine from '../../layouts/DividerLine';
import TestComponent from '../Products/TestComponent';
import TagSelect from '../Products/TagSelect';
import getAllPanelCode from '../../hooks/getAllPanelCode';
import CommentComponent from '../Products/CommentComponent';

const EMPTY_RANGE = {
  type: '',
  rangeFrom: '',
  rangeTo: '',
  dailBandColor: '#ffffff',
  isDefault: false,
};

const POS_NEG_OPTIONS = [
  { name: 'Positive', value: 'POSITIVE' },
  { name: 'Negative', value: 'NEGATIVE' },
];

const CLASS_OPTIONS = [
  { name: '0', value: '0' },
  { name: '1', value: '1' },
  { name: '2', value: '2' },
  { name: '3', value: '3' },
  { name: '4', value: '4' },
  { name: '5', value: '5' },
  { name: '6', value: '6' },
];

const LabTestCreate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // UI / control state
  const [loader, setLoader] = useState(false);
  const [show, setShow] = useState(false);

  // Data state
  const [labTestObj, setLabTestObj] = useState({});
  const [addUserResp, setAddUserResp] = useState({});
  const [uploadObj, setUploadObj] = useState({});

  // Content state
  const [textEditorValue, settextEditorValue] = useState();
  const [comments, setComments] = useState('');

  // Result type + ranges
  const [resType, setResType] = useState(''); // 'R' | 'S' | 'P' | 'C'
  const [testRanges, setTestRanges] = useState([]); // single source of truth for ranges

  // Video state
  const [uploadVideo, setuploadVideo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isShowRmv, setisShowRmv] = useState(false);

  // Panels (tags)
  const [cisPanelNames, setCisPanelNames] = useState([]);

  // Color helper state (only for inline input preview)
  const [newColor, setNewColor] = useState('');

  // Hooks / derived
  const cisPanelCodeRes = getAllPanelCode();
  const cisPanelCode = cisPanelCodeRes.response ? cisPanelCodeRes.response : [];

  // ---- Navigation: handle back ----
  useEffect(() => {
    const goBack = () => navigate('/labtest/view', { replace: true });
    window.addEventListener('popstate', goBack);
    return () => window.removeEventListener('popstate', goBack);
  }, [navigate]);

  // ---- Helpers ----
  const getBase64 = (file, cb) => {
    const reader = new FileReader();
    reader.onload = () => cb && cb(reader.result);
    reader.readAsDataURL(file);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'video/x-matroska', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      message.error('You can only upload JPG/PNG/MKV/MP4 files!');
      return Upload.LIST_IGNORE;
    }
    const sizeMB = file.size / 1024 / 1024;
    const isVideo = file.type === 'video/mp4' || file.type === 'video/x-matroska';
    const maxMB = isVideo ? 200 : 20;
    if (sizeMB > maxMB) {
      message.error(isVideo ? 'Video must be smaller than 200MB!' : 'Image must be smaller than 20MB!');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleuploadChange = (info) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      const url = info.file?.response?.result?.data?.Location || info.file?.response?.result?.data?.Location || info.file?.response?.result?.data?.location || info.file?.url || null;
      if (url) {
        setuploadVideo(url);
        setisShowRmv(true);
      } else {
        console.warn('Could not find uploaded URL in server response', info.file.response);
        message.warning('Upload finished but response parsing failed (check console).');
        setisShowRmv(false);
      }
      if (info.file.originFileObj) {
        getBase64(info.file.originFileObj, () => {
          // local preview if you need it
        });
      }
      return;
    }
    if (info.file.status === 'error') {
      setUploading(false);
      message.error('Upload failed.');
      setisShowRmv(false);
      return;
    }
    if (info.file.status === 'removed') {
      setuploadVideo('');
      setisShowRmv(false);
      setUploading(false);
    }
  };

  // Toggle back to saved video
  const labTestToggleVideo = () => {
    setuploadVideo('');
    setTimeout(() => {
      setuploadVideo(labTestObj?.panelTestVideo || '');
      if (labTestObj?.panelTestVideo) setisShowRmv(true);
    }, 500);
  };

  // ---- ResultType change => initialize / clear ranges ----
  const initializeRangesForType = useCallback(
    (type) => {
      // P: we still want one line for POSITIVE/NEGATIVE selection
      if (type === 'P') {
        const row = { ...EMPTY_RANGE, type: '' };
        setTestRanges([row]);
        form.setFieldsValue({ ranges: [row] });
        return;
      }
      if (type === 'R' || type === 'C') {
        const row = { ...EMPTY_RANGE };
        setTestRanges([row]);
        form.setFieldsValue({ ranges: [row] });
        return;
      }
      // S or anything else => clear ranges
      setTestRanges([]);
      form.setFieldsValue({ ranges: [] });
    },
    [form]
  );

  // ---- Handlers ----
  const handleResultTypeChange = (type) => {
    setResType(type);
    initializeRangesForType(type);
  };

  const handleEditorChange = (html) => settextEditorValue(html);
  const handleCommentChange = (html) => setComments(html);
  const handleContentDisplay = (v) => {
    // reserved for future conditional UI if needed
  };

  // ---- Submission ----
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }, []);

    const putLabTestListing = async (details) => {
        console.log('details', details);
        // return 
    try {
      setLoader(true);
      const res = await axios.post(`${app.URL}/api/v1/admin/labtest/add`, details, { headers: getHeaders() });
      setLoader(false);
      setShow(false);
      if (res?.data?.result?.success) {
        setAddUserResp(res.data.result.data);
             message.success("Panel Test added successfully.");
        // message.success(res.data.result.msg || 'Updated!');
        setTimeout(() => navigate('/labtest/view', { replace: true }), 500);
      } else {
        message.warning(res?.data?.result?.msg || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      setLoader(false);
      setShow(false);
      message.error(err?.response?.data?.message || 'Server error');
    }
  };

  const onFinish = (values) => {
    const finalRanges = ['R', 'C', 'P'].includes(resType) ? testRanges : [];
    if ((resType === 'R' || resType === 'P') && (!finalRanges || finalRanges.length === 0)) {
      message.info('Please add minimum one range for result!');
      return;
    }
    if (values.resultTestCode.trim() == '' || values.resultElement.trim() == '' || values.displayName.trim() == '' ) {
    
      message.info('Must fill all fields cannot set empty  field');
      return; 
    }
if(cisPanelNames.length==0) return message.info('Please add Cis PanelName');
    const payload = {
      resultTestCode: values.resultTestCode || null,
      resultElement: values.resultElement || null,
      resultElementGroupId: cisPanelNames,
      displayPosition: values.displayPosition || '',
      displayName: values.displayName || '',
      isActive: values.isActive ? 1 : 0,
      description: textEditorValue,
      resultType: resType || null,
      ranges: finalRanges,
      videoUrl: uploadVideo || null,
      // viewType: values.viewType || null,
      viewType: values.viewType ? (values.viewType == 'None' ? 'none' : values.viewType) : 'none',
      unit: values.unit || null,
      comment: comments,
      userId: user?.result?.data?.user?.userId,
    };

    setUploadObj(payload);
    setShow(true);
  };

  const submitData = () => {
    if (Object.keys(uploadObj || {}).length > 0) {
      putLabTestListing(uploadObj);
    }
  };

  // ---- Range field updaters (immutable) ----
  const updateRangeAt = (index, patch) => {
    setTestRanges((prev) => {
      const next = [...prev];
      const current = next[index] || { ...EMPTY_RANGE };
      next[index] = { ...current, ...patch };
      // sync into Form.List too
      form.setFieldsValue({ ranges: next });
      return next;
    });
  };

  // ---- Render ----
  return (
    <UpdateRecord
      pageTitle='Add Panel Test'
      loading={loader}
      response={labTestObj}
      goBack={() => navigate('/labtest/view', { replace: true })}
      upload={false}
      form={form}
      show={show}
      handleClose={() => setShow(false)}
      addUserResp={addUserResp}
      submitData={submitData}
      updateMessage='Are you sure you want to add lab test element?'
      initialValues={{
        remember: false,
        resultElement: '',
        resultTestCode: '',
        resultType: '',
        // viewType: '',
        displayPosition: '',
        displayName: '',
        isActive: true,
        ranges: [],
        unit: '',
      }}
      onFinish={onFinish}
      onFinishFailed={() => {}}
      switchBtn={false}
      inputs={[
        <div className='col-lg-6' key='panel-tags'>
          <TagSelect
            options={cisPanelCode.map((item) => {
              if (!cisPanelNames.includes(item.resultElementGroupId)) {
                return (
                  <Select.Option value={item.resultElementGroupId} key={item.resultElementGroupId}>
                    {item.cisPanelName} ({item.cisPanelCode})
                  </Select.Option>
                );
              }
              return null;
            })}
            col='col-12'
            tags={cisPanelNames}
            setTags={setCisPanelNames}
            label='Panel Name'
            cisPanelCode={cisPanelCode}
            isPanelCode={true}
          />
        </div>,

        <div className='col-lg-6 d-none d-md-block' key='spacer' />,

        <InputField
          col='col-lg-6'
          name='resultTestCode'
          disabled={false}
          placeholder='Test Code'
          label='Test Code'
          key='test-code'
          rules={[
            {
              required: true,
              message: 'Enter  Code',
            },
          ]}
          required={true}
        />,
        <InputField
          col='col-lg-6'
          name='resultElement'
          disabled={false}
          placeholder='Element'
          label='Test Name'
          key='test-name'
          rules={[
            {
              required: true,
              message: 'Enter Element',
            },
          ]}
          required={true}
        />,

        <InputField col='col-lg-4' name='displayName' required={true} rules={[{ required: true, message: 'Enter Test Display Name' }]} placeholder='Test Display Name' label='Test Display Name' key='display-name' />,

        <InputField col='col-lg-2' name='unit'  placeholder='Test Unit' label='Test Unit' key='unit' />,

        <SelectField
          col='col-lg-3'
          name='resultType'
          label='Result Type'
          onChange={handleResultTypeChange}
          rules={[{ required: true, message: 'Select Result Type' }]}
          placeholder='Result Type'
          required={true}
          key='result-type'
          options={[
            { name: 'Range', value: 'R' },
            { name: 'Single Value', value: 'S' },
            { name: 'Positive/Negative', value: 'P' },
            { name: 'Class', value: 'C' },
          ].map((item) => (
            <Select.Option value={item.value} key={item.value}>
              {item.name}
            </Select.Option>
          ))}
        />,

        <InputField col='col-lg-3' name='displayPosition' label='Display Position' key='display-position' />,

        <div className='col-lg-9' key='comments'>
          <CommentComponent title='Comments' onChange={setComments} textvalue={comments} />
        </div>,

        <div className='col-lg-3 ' key='active-switch'>
          <Form.Item label='Active' className='selector-main' colon={false} valuePropName='checked' name='isActive'>
            <Switch />
          </Form.Item>
        </div>,

        <DividerLine key='divider-1' />,

        <div className='col-lg-8' key='view-type'>
          <div className='row'>
            <SelectField
              col='col-lg-6'
              name='viewType'
              onChange={handleContentDisplay}
              defaultVal='None'
              options={[
                { value: 'none', name: 'None' },
                { value: 'video', name: 'Video' },
                { value: 'text', name: 'Text' },
                { value: 'both', name: 'Both' },
              ].map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.name}
                </Select.Option>
              ))}
              // rules={[{ required: true, message: 'Select Content Display' }]}
              // required={true}
              label='Show Description'
            />
          </div>
        </div>,

        <div className='col-lg-8' key='description'>
          <TestComponent title='Description' onChange={settextEditorValue} textvalue={textEditorValue} />
        </div>,

        <div className='col-md-4 col-12 mb-lg-0 mb-2' key='video'>
          {uploadVideo && <label>Video</label>}
          {uploadVideo && (
            <video style={{ width: '100%', borderRadius: '5px', boxShadow: '3px 3px 10px #ccc' }} controls title='YouTube video player'>
              <source src={uploadVideo} type='video/mp4' />
            </video>
          )}
          <div className='d-flex gap-1'>
            <Form.Item colon={false} name='video' className='mt-2'>
              <Space direction='vertical' style={{ width: '100%' }} size='large'>
                <Upload showUploadList={true} onRemove={labTestToggleVideo} listType='picture' maxCount={1} className='avatar-uploader' headers={getHeaders()} action={`${app.URL}/api/v1/general/product/videoUpload`} beforeUpload={beforeUpload} onChange={handleuploadChange}>
                  <Button icon={<UploadOutlined />} className='upload-btn' style={{ color: 'black' }} loading={uploading}>
                    Upload Video
                  </Button>
                </Upload>
              </Space>
            </Form.Item>

            {isShowRmv && (
              <>
                <div>
                  <button
                    style={{ backgroundColor: '#F21D2F' }}
                    onClick={() => {
                      setuploadVideo('');
                      setisShowRmv(false);
                    }}
                    className='mt-2 remove-btn'
                  >
                    Remove
                  </button>
                </div>
                <br />
              </>
            )}
          </div>
        </div>,
      ]}
      extras={
        ['P', 'R', 'C'].includes(resType) && [
          <div className='col-lg-12 mb-4' key='ranges-block'>
            <h2 className='dynamic-field-head mb-0'>Ranges</h2>
            <table id='customers' className='px-0' style={{ width: `${resType === 'P' ? '55%' : '100%'}` }}>
              <thead>
                {resType === 'P' ? (
                  <tr>
                    <th>Normal Ranges</th>
                    <th></th>
                  </tr>
                ) : (
                  <tr>
                    <th style={{ width: '15%', paddingLeft: '1rem', textAlign: 'left' }}>Type</th>
                    <th style={{ width: '15%', paddingLeft: '1.1rem', textAlign: 'left' }}>Start</th>
                    <th style={{ width: '15%', paddingLeft: '2.1rem', textAlign: 'left' }}>End</th>
                    <th style={{ width: '15%', paddingLeft: '3rem', textAlign: 'left' }}>Color</th>
                    <th style={{ width: '40%', paddingLeft: '1rem' }}></th>
                  </tr>
                )}
              </thead>
            </table>

            <Form.List name='ranges'>
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <RangesCollection
                      key={key}
                      name={name}
                      restField={restField}
                      resType={resType}
                      colorsCodes={[]}
                      testRanges={testRanges}
                      updateRangeAt={updateRangeAt}
                      remove={(n) => {
                        // Remove from Form.List
                        remove(n);
                        // Remove from local state immutably
                        setTestRanges((prev) => {
                          const next = prev.filter((_, idx) => idx !== n);
                          form.setFieldsValue({ ranges: next });
                          return next;
                        });
                      }}
                    />
                  ))}

                  {(resType === 'R' || resType === 'C') && (
                    <div className='row px-2'>
                      <div
                        style={{ textAlign: 'center' }}
                        onClick={() => {
                          add();
                          setTestRanges((prev) => {
                            const next = [...prev, { ...EMPTY_RANGE }];
                            form.setFieldsValue({ ranges: next });
                            return next;
                          });
                        }}
                        className='add-btn col-md-3'
                      >
                        Add Range
                      </div>
                    </div>
                  )}

                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </div>,
        ]
      }
    />
  );
};

const RangesCollection = ({ resType, name, restField, testRanges, updateRangeAt, remove }) => {
  const index = name;

  const renderOptions = useMemo(() => {
    if (resType === 'P') return POS_NEG_OPTIONS;
    if (resType === 'C') return CLASS_OPTIONS;
    return [];
  }, [resType]);

  const handleTypeChange = (valOrEvt) => {
    const value = valOrEvt?.target?.value ?? valOrEvt; // supports Input vs Select
    updateRangeAt(index, { type: value });
  };

  const handleStartChange = (e) => updateRangeAt(index, { rangeFrom: e?.target?.value ?? e });
  const handleEndChange = (e) => updateRangeAt(index, { rangeTo: e?.target?.value ?? e });

  const handleColorInput = (e) => {
    const hex = e?.target?.value ?? '';
    updateRangeAt(index, { dailBandColor: hex });
  };

  // antd 4 ColorPicker is not standard; keeping your previous signature
  const handleColorPicker = (_value, hex) => {
    updateRangeAt(index, { dailBandColor: hex });
  };

  const handleDefaultChange = (e) => {
    updateRangeAt(index, { isDefault: !!e?.target?.checked });
  };

  return (
    <div className='row mb-3 mb-md-0 px-1' key={`range-row-${index}`}>
      {(resType === 'P' || resType === 'C') && (
        <SelectField
          col={resType === 'C' ? 'col-2' : 'col-6'}
          name={[name, 'type']}
          onChange={handleTypeChange}
          rules={[{ required: true, message: 'Select Type' }]}
          placeholder='Type'
          {...restField}
          options={renderOptions.map((item) => (
            <Select.Option value={item.value} key={item.value}>
              {item.name}
            </Select.Option>
          ))}
        />
      )}

      {resType === 'R' && <TextAreaField rows={1} col='col-2' name={[name, 'type']} onChange={handleTypeChange} rules={[{ required: true, message: 'Enter Type' }]} placeholder='Enter Type' {...restField} />}

      {(resType === 'R' || resType === 'C') && <InputField col='col-2' name={[name, 'rangeFrom']} onChange={handleStartChange} rules={[{ required: true, message: 'Enter Start Range' }]} placeholder='Start Range' {...restField} />}

      {(resType === 'R' || resType === 'C') && <InputField col='col-2' name={[name, 'rangeTo']} onChange={handleEndChange} rules={[{ required: true, message: 'Enter End Range' }]} placeholder='End Range' {...restField} />}

      {(resType === 'C' || resType === 'R') && <InputField col='col-2' name={[name, 'dailBandColor']} onChange={handleColorInput} rules={[{ required: true, message: 'Enter Color' }]} placeholder='Color' {...restField} />}

      {(resType === 'R' || resType === 'C') && <ColorPicker format='hex' disabled={false} onChange={handleColorPicker} className='col-1' />}

      {resType === 'C' && (
        <div className='col-1 gap-1 d-flex align-items-start'>
          <Checkbox onChange={handleDefaultChange}>Default</Checkbox>
        </div>
      )}

      {resType === 'R' && (
        <div className='col-1 gap-1 text-center text-danger cursor-pointer' onClick={() => remove(name)}>
          <DeleteFilled className='fs-5' />
        </div>
      )}
    </div>
  );
};

export default LabTestCreate;
